import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Box, Typography, Button, Avatar, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
const isIOS = () => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined')
        return false;
    const isIOSDevice = [
        'iPad Simulator',
        'iPhone Simulator',
        'iPod Simulator',
        'iPad',
        'iPhone',
        'iPod',
    ].includes(navigator.platform);
    const isIPadOS =
        navigator.userAgent.includes('Mac') && 'ontouchend' in document;

    return isIOSDevice || isIPadOS;
};

const CustomAppBanner = () => {
    const { t } = useTranslation();
    const [isVisible, setIsVisible] = useState(false);
    const [showBannerState, setShowBannerState] = useState(true);
    const location = useLocation();

    // Get user from Redux store
    const currentUser = useSelector((state) => state.core.user);
    const isLoggedIn = !!currentUser?.uid;

    const APP_STORE_URL =
        'https://apps.apple.com/ca/app/bos-node/id6744272413?l=fr-CA';
    const APP_ICON_PATH = '/assets/v3/img/IconBOS.jpg';
    const APP_NAME = 'BOS - Node';
    const DEVELOPER_NAME = 'Gérez où que vous soyez.';

    const isOnBosProductPage = location.pathname.startsWith('/products/bos');
    const isOnSelectBusinessPage = location.pathname === '/select-business';

    useEffect(() => {
        const bannerClosedSession = sessionStorage.getItem('customAppBannerClosed');
        const initiallyShow = !bannerClosedSession;
        const shouldShowForRoute = isOnBosProductPage || isOnSelectBusinessPage;

        const shouldShow =
            isLoggedIn &&
            isIOS() &&
            initiallyShow &&
            showBannerState &&
            shouldShowForRoute;

        setIsVisible(shouldShow);

        if (bannerClosedSession) {
            setShowBannerState(false);
        }
    }, [isLoggedIn, showBannerState, location.pathname]);

    const handleClose = () => {
        setShowBannerState(false);
        sessionStorage.setItem('customAppBannerClosed', 'true');
    };

    if (!isVisible) {
        return null;
    }

    return (
        <Box
            sx={{
                position: 'sticky',
                top: isOnBosProductPage ? '50px' : '1px',
                left: 0,
                right: 0,
                zIndex: 1300,
                display: 'flex',
                alignItems: 'center',
                padding: '8px 12px',
                backgroundColor: 'hsla(0, 0%, 97%, .8)',
                backdropFilter: 'saturate(180%) blur(20px)',
                WebkitBackdropFilter: 'saturate(180%) blur(20px)',
                borderBottom: '1px solid rgba(128, 128, 128, 0.2)',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                minHeight: '70px',
            }}
        >
            <IconButton
                size="small"
                onClick={handleClose}
                sx={{
                    position: 'absolute',
                    left: 8,
                    top: 8,
                    color: 'grey.500',
                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                }}
                aria-label="Fermer la bannière"
            >
                <CloseIcon fontSize="inherit" sx={{ fontSize: '16px' }} />
            </IconButton>

            <Avatar
                src={APP_ICON_PATH}
                variant="rounded"
                sx={{
                    width: 42,
                    height: 42,
                    marginLeft: '40px',
                    marginRight: '12px',
                    marginTop: '2px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                }}
                alt={`Icône ${APP_NAME}`}
            />

            {/* App Info */}
            <Box sx={{ flexGrow: 1, marginRight: '12px', overflow: 'hidden' }}>
                <Typography
                    noWrap
                    variant="body2"
                    sx={{ fontWeight: '600', lineHeight: 1.3, fontSize: '0.9rem' }}
                >
                    {APP_NAME}
                </Typography>
                <Typography
                    noWrap
                    variant="caption"
                    sx={{ color: 'text.secondary', lineHeight: 1.2, fontSize: '0.75rem' }}
                >
                    {DEVELOPER_NAME}
                </Typography>
            </Box>

            {/* View Button */}
            <Button
                variant="contained"
                size="small"
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                    textTransform: 'none',
                    borderRadius: '12px',
                    padding: '3px 12px',
                    fontSize: '0.8rem',
                    fontWeight: 'bold',
                    backgroundColor: '#f0f0f0',
                    color: '#007aff',
                    boxShadow: 'none',
                    border: '1px solid rgba(0, 122, 255, 0.3)',
                    '&:hover': {
                        backgroundColor: '#e5e5e5',
                        boxShadow: 'none',
                    },
                }}
            >
                {t('download')}
            </Button>
        </Box>
    );
};

export default CustomAppBanner;